// src/services/api.js
import axios from 'axios';

// Create an Axios instance
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL, // Use the env variable
    headers: {
        'Content-Type': 'application/json',
    },
});

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('publicAuthToken'); // <<< Use key from AuthContext
        if (token) {
            config.headers['Authorization'] = `${token}`;
        }
        return config;
    }, /* ... error handling ... */);

// ... getActiveServices, getServiceById ...

// Add Login (if not already there)
export const login = (email, password) => {
    return apiClient.post('/auth/login', { email, password });
};

// Add Register
export const register = (userData) => {
    // userData = { name, email, password }
    return apiClient.post('/auth/register', userData);
};

export const zampassLogin = (site) => {
    return apiClient.get('/auth/zampassLogin', {
        params: {
            site
        }
    });
};

export const zampassLogout = (site, zamPassIdToken) => {
    return apiClient.get('/auth/zampassLogout', {
        params: {
            site,
            zamPassIdToken
        }
    });
};

export const completeRegistration = (data) => {
    return apiClient.post('/auth/completeRegistration', data);
};

// api.js (RECOMMENDED AXIOS STYLE)
export const zampassRedirectLogin = (code, state, site) => {
    return apiClient.get('/auth/zampassRedirectLogin', {
        // Axios knows to serialize the contents of 'params' into the URL
        params: {
            code,
            state,
            site
        }
    });
};


// Add GetMe (if not already there)
export const getMe = () => {
    return apiClient.get('/auth/me');
};

export const getMdas = () => {
    return apiClient.get('/mdas');
};

// Function to get active services
export const getActiveServices = (params = {}) => { // <<< Accept optional params
    const queryParams = { ...params, activeOnly: true };
    return apiClient.get('/services', { params: queryParams });
};

// Function to get a single service by ID
export const getServiceById = (id) => {
    return apiClient.get(`/services/${id}`);
};

// Modify submitCase - remove applicant details
export const submitCase = (serviceId, caseId, formData) => {
    // applicantName, applicantEmail are removed - backend gets from token
    return apiClient.post('/cases', {
        serviceId,
        caseId,
        formData
    });
};

export const insertCase = (params = {}) => { // <<< Accept optional params object
    return apiClient.post('/cases/insertCase', { params }); // <<< Pass params here
}

export const insertFlowerCase = (params = {}) => { // <<< Accept optional params object
    return apiClient.post('/cases/insertFlowerCase', { params }); // <<< Pass params here
}

export const insertRentalPaymentCase = (params = {}) => {
    return apiClient.post('/cases/insertRentalPaymentCase', { params });
};

export const signLease = (id) => {
    return apiClient.get(`/cases/signLease/${id}`,);
};

export const getCases = (params = {}) => {
    return apiClient.get('/cases', { params });
};

export const getUserIdCases = (params = {}) => {
    return apiClient.get('/cases/usercases', { params });
};

export const getRentalCases = (params = {}) => {
    return apiClient.get('/cases/getRentalCases', { params });
};

export const getUserRentalCases = (params = {}) => {
    return apiClient.get('/cases/getUserRentalCases', { params });
};

export const renderDocument = ({ CaseID, documentType }) => {
    return apiClient.get(`/cases/getDoc/${CaseID}/document/${documentType}/render?out=json`);
};

export const getDocument = (filename) => {
    return apiClient.get(
        `/cases/getDoc/view/${filename}`,
        {
            // 💡 CRITICAL: Ensure you request the response as a blob/binary
            responseType: 'blob'
        }
    );
};

// Function to download case as PDF
export const downloadCasePdf = (caseId) => {
    return apiClient.get(`/cases/${caseId}/download-pdf`, {
        responseType: 'blob' // Important for downloading binary data
    });
};

// Function to upload a document for a case
export const uploadDocument = (caseId, file, documentType) => {
    const formData = new FormData();
    formData.append('documentFile', file);
    formData.append('documentType', documentType);

    return apiClient.post(`/cases/${caseId}/documents`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const updateProfile = (userData) => {
    return apiClient.put('/users/profile', userData);
};

// =========================================================
//                  ZAMPAY ENDPOINTS
// =========================================================
export const createInvoice = (caseId) => {
    // Hits the route GET /zampay/createInvoice/:caseId
    return apiClient.get(`/zampay/createInvoice/${caseId}`);
};

export const checkPaymentStatus = (invoiceNumber) => {
    // Hits the route GET /zampay/createInvoice/:caseId
    return apiClient.get(`/zampay/checkPaymentStatus/${invoiceNumber}`);
};

export const checkPaymentStatusNoAuth = (invoiceNumber) => {
    // Hits the route GET /zampay/createInvoice/:caseId
    return apiClient.get(`/zampay/checkPaymentStatusNoAuth/${invoiceNumber}`);
};

export const updatePaymentStatus = (caseId, invoiceNumber) => {
    // Hits the route GET /zampay/createInvoice/:caseId
    return apiClient.get(`/zampay/updatePaymentStatus/${caseId}/Invoice/${invoiceNumber}`);
};

// =========================================================
//                  Provinces/Districts
// =========================================================
export const getAllProvinces = () => {
    return apiClient.get(`/provinces`);
};
export const getDistrictsByProvince = (id) => {
    return apiClient.get(`/districts/${id}`);
};

export const getProducts = () => {
    return apiClient.get('/products');
};

// --- Lease Agreements ---
export const getLeaseAgreements = (params = {}) => {
    return apiClient.get('/lease-agreements', { params });
};

export const getLeaseAgreementsRent = (params = {}) => {
    return apiClient.get('/lease-agreements/rental', { params });
};

export const getPublicLeaseBillings = (leaseId) => {
    return apiClient.get(`/rent-billings/public/lease/${leaseId}`);
};

export const getLeaseAgreementById = (id) => {
    return apiClient.get(`/lease-agreements/${id}`);
};

// =========================================================
//                  Proxy Search Endpoints
// =========================================================
export const searchPacraCompany = (searchTerm) => {
    return apiClient.get('/proxy/pacra-search', {
        params: { searchTerm }
    });
};

export const searchZraTpin = (brn) => {
    return apiClient.post('/proxy/zra-tpin-search', { brn });
};

export default apiClient; // Optional: export the instance if needed directly